import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataGrid,
  DataGridColumnSizingState,
  DataGridLeafColumn,
  DataGridSortingState,
  Spinner,
} from '@ibsys/kit';
import {
  RenessansOperatorItem,
  RenessansOperatorsResponse,
  RenessansOperatorsSortBy,
} from '@/entities/renessans-dashboards';
import { TalkIntervalsSparkline } from './TalkIntervalsSparkline';
import { formatHHMMSS, formatInt, formatMMSS } from './utils';

type Props = {
  data: RenessansOperatorsResponse | undefined;
  isLoading: boolean;
  page: number;
  perPage: number;
  sortBy: RenessansOperatorsSortBy;
  sortDir: 'asc' | 'desc';
  /** Текущие ширины колонок (px); приходит из useTableState */
  columnSizing: DataGridColumnSizingState | undefined;
  onChangePagination: (page: number, perPage: number) => void;
  onChangeSorting: (sortBy: RenessansOperatorsSortBy, sortDir: 'asc' | 'desc') => void;
  onColumnSizingChange: (
    value: DataGridColumnSizingState | ((prev: DataGridColumnSizingState) => DataGridColumnSizingState),
  ) => void;
};

/** camelCase id колонки → snake_case поле для бэкенда. */
const SORT_FIELD_MAP: Record<string, RenessansOperatorsSortBy> = {
  callsCount: 'calls_count',
  avgDuration: 'avg_duration',
  minDuration: 'min_duration',
  maxDuration: 'max_duration',
  totalDuration: 'total_duration',
};

const SORT_FIELD_INVERSE: Record<RenessansOperatorsSortBy, string> = {
  calls_count: 'callsCount',
  avg_duration: 'avgDuration',
  min_duration: 'minDuration',
  max_duration: 'maxDuration',
  total_duration: 'totalDuration',
};

const ROW_HEIGHT = 36;
const GRID_OVERHEAD = 120;

/**
 * Виджет 4 «Статистика по операторам»: серверная таблица на DataGrid
 * с пагинацией и сортировкой по числовым колонкам.
 * Строка итогов рендерится под таблицей.
 */
export const OperatorsTable: FC<Props> = ({
  data,
  isLoading,
  page,
  perPage,
  sortBy,
  sortDir,
  columnSizing,
  onChangePagination,
  onChangeSorting,
  onColumnSizingChange,
}) => {
  const { t } = useTranslation('renessansDashboard');

  const columns = useMemo<DataGridLeafColumn<RenessansOperatorItem>[]>(
    () => [
      {
        id: 'operatorName',
        accessor: 'operatorName',
        header: t('operators.operatorName'),
        size: 240,
        cell: ({ row }) => row.operatorName,
      },
      {
        id: 'callsCount',
        accessor: 'callsCount',
        header: t('operators.callsCount'),
        size: 180,
        enableSorting: true,
        cell: ({ row }) => formatInt(row.callsCount),
      },
      {
        id: 'avgDuration',
        accessor: 'avgDuration',
        header: t('operators.avgDuration'),
        size: 200,
        enableSorting: true,
        cell: ({ row }) => formatMMSS(row.avgDuration),
      },
      {
        id: 'minDuration',
        accessor: 'minDuration',
        header: t('operators.minDuration'),
        size: 200,
        enableSorting: true,
        cell: ({ row }) => formatMMSS(row.minDuration),
      },
      {
        id: 'maxDuration',
        accessor: 'maxDuration',
        header: t('operators.maxDuration'),
        size: 200,
        enableSorting: true,
        cell: ({ row }) => formatMMSS(row.maxDuration),
      },
      {
        id: 'totalDuration',
        accessor: 'totalDuration',
        header: t('operators.totalDuration'),
        size: 200,
        enableSorting: true,
        cell: ({ row }) => formatHHMMSS(row.totalDuration),
      },
      {
        id: 'talkIntervals',
        accessor: 'talkIntervals',
        header: t('operators.talkIntervals'),
        size: 220,
        enableSorting: false,
        wrap: true,
        cell: ({ row }) => <TalkIntervalsSparkline items={row.talkIntervals} />,
      },
    ],
    [t],
  );

  const sorting = useMemo<DataGridSortingState>(
    () => [{ columnId: SORT_FIELD_INVERSE[sortBy], direction: sortDir }],
    [sortBy, sortDir],
  );

  const handleSortingChange = (next: DataGridSortingState) => {
    const first = next[0];
    if (!first) {
      onChangeSorting('calls_count', 'desc');
      return;
    }
    const mapped = SORT_FIELD_MAP[first.columnId];
    if (!mapped) return;
    onChangeSorting(mapped, first.direction);
  };

  const handlePageChange = (nextPageZeroBased: number) => {
    onChangePagination(nextPageZeroBased + 1, perPage);
  };

  const handlePageSizeChange = (nextSize: number) => {
    onChangePagination(1, nextSize);
  };

  const operators = data?.operators ?? [];
  const totals = data?.totals;
  const gridHeight = Math.max(operators.length, 1) * ROW_HEIGHT + GRID_OVERHEAD;

  console.log(columns);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <CardTitle>{t('operators.title')}</CardTitle>
        {isLoading && <Spinner className="size-4" />}
      </CardHeader>
      <CardContent className="p-0">
        <DataGrid<RenessansOperatorItem>
          columns={columns}
          dataSource={{
            mode: 'paginated',
            totalCount: data?.total ?? 0,
            page: Math.max(0, page - 1),
            pageSize: perPage,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
            pageSizeOptions: [perPage],
            data: operators,
          }}
          rowHeight={86}
          getRowId={(row) => String(row.operatorId)}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          enableColumnResizing
          columnSizing={columnSizing}
          onColumnSizingChange={onColumnSizingChange}
          height={gridHeight}
        />
        {totals && operators.length > 0 && (
          <div className="grid grid-cols-7 gap-2 px-4 py-3 border-t bg-muted/40 text-sm font-medium">
            <div>{t('operators.totals')}</div>
            <div className="text-right">{formatInt(totals.callsCount)}</div>
            <div className="text-right">{formatMMSS(totals.avgDuration)}</div>
            <div className="text-right">{formatMMSS(totals.minDuration)}</div>
            <div className="text-right">{formatMMSS(totals.maxDuration)}</div>
            <div className="text-right">{formatHHMMSS(totals.totalDuration)}</div>
            <div>
              <TalkIntervalsSparkline items={totals.talkIntervals} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
